import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import {
  JPBManager,
} from "src/modules/JPBModule/JPBManager";
import type { JPBMapLayerHandlers } from "src/modules/JPBModule/JPBMapLayers";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import {
  AreaUnit,
  PriceMode,
  type JPBAssumptions,
  type JPBReferenceProperty,
} from "src/modules/JPBModule/JPBState";

export const SKOV_SOURCE_ID_POLY = "jpb-skov-demo-reference-properties-poly";
export const SKOV_SOURCE_ID = "jpb-skov-demo-reference-properties";
export const SKOV_SOURCE_ID_HOVER = "jpb-skov-demo-reference-properties-hover-labels";
export const SKOV_SOURCE_ID_HIGHLIGHT = "jpb-skov-demo-row-highlight";
export const SKOV_LAYER_ID = "jpb-skov-demo-reference-properties-layer";
export const SKOV_LAYER_ID_ISSUE = "jpb-skov-demo-reference-properties-issue-layer";
export const SKOV_LAYER_ID_BUILDING = "jpb-skov-demo-reference-properties-building-layer";
export const SKOV_LAYER_ID_POLY = "jpb-skov-demo-reference-properties-layer-poly";
export const SKOV_LAYER_ID_HIGHLIGHT = "jpb-skov-demo-row-click-highlight-layer";
export const SKOV_TEXT_LAYER_ID = "jpb-skov-demo-reference-properties-text";
export const SKOV_ORIGINAL_TEXT_LAYER_ID =
  "jpb-skov-demo-reference-properties-text-original";

const JPB_ICON_CHECKED = "jpb-house-checked";
const JPB_ICON_UNCHECKED = "jpb-house-unchecked";
const JPB_ICON_LOCKED = "jpb-house-locked";

async function ensureJPBBuildingIcons(map: MaplibreMap): Promise<void> {
  const iconConfigs = [
    { id: JPB_ICON_CHECKED, size: 26, iconScale: 1 },
    { id: JPB_ICON_UNCHECKED, size: 26, iconScale: 1 },
    { id: JPB_ICON_LOCKED, size: 26, iconScale: 1 },
  ];

  for (const config of iconConfigs) {
    if (map.hasImage(config.id)) {
      continue;
    }

    try {
      const dataUrl = await GenerateGeom.reactIconMarker(MIBIcon, {
        icon: "house",
        iconColor: "#ffffff",
        size: config.size,
        iconScale: config.iconScale || 1,
        strokeWidth: 1.1,
      });

      const image = await map.loadImage(dataUrl);
      if (!map.hasImage(config.id)) {
        map.addImage(config.id, image.data, { pixelRatio: 2 });
      }
    } catch (error) {
      console.error(`Failed to create JPB building icon ${config.id}:`, error);
    }
  }
}

function parseGeoshape(prop: JPBReferenceProperty): GeoJSON.Geometry | null {
  let g: any = prop.geoshape;
  if (typeof g === "string") {
    try {
      g = JSON.parse(g);
    } catch {
      return null;
    }
  }
  if (!g || (g.type !== "Polygon" && g.type !== "MultiPolygon")) {
    return null;
  }
  return g;
}

function buildSkovPolyGeoJson(
  properties: JPBReferenceProperty[],
  checkedItems: Set<string>,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const prop of properties) {
    const geometry = parseGeoshape(prop);
    if (!geometry) continue;
    features.push({
      id: prop.documentID,
      type: "Feature",
      geometry,
      properties: {
        bfe_nr: prop.bfe_nr,
        documentID: prop.documentID,
        checked: checkedItems.has(prop.documentID),
        pricePerSquareMeter: prop.pricePerSquareMeter,
      },
    });
  }
  return { type: "FeatureCollection", features };
}

function setupSkovTextLayers(
  map: MaplibreMap,
  priceMode: PriceMode,
  areaUnit: AreaUnit,
) {
  let formattedPriceProperty: string;
  let textFilter: any = null;
  const isHectare = areaUnit === AreaUnit.Hectare;
  const unitLabel = isHectare ? " kr/ha" : " kr/m²";

  if (priceMode === PriceMode.Landbrug) {
    formattedPriceProperty = isHectare
      ? "formattedLandbrugHa"
      : "formattedLandbrugM2";
    textFilter = [
      "all",
      ["has", "effectiveMarkPrice"],
      [">", ["get", "effectiveMarkPrice"], 0],
    ];
  } else {
    formattedPriceProperty = isHectare
      ? "formattedStandardHa"
      : "formattedStandardM2";
  }

  const textFieldExpression = ["concat", ["get", formattedPriceProperty], unitLabel];

  if (map.getLayer(SKOV_TEXT_LAYER_ID)) {
    map.removeLayer(SKOV_TEXT_LAYER_ID);
  }

  const textLayerSpec: any = {
    id: SKOV_TEXT_LAYER_ID,
    type: "symbol",
    source: SKOV_SOURCE_ID,
    layout: {
      "text-field": textFieldExpression,
      "text-font": ["Noto Sans Bold"],
      "text-size": 16,
      "text-anchor": "top",
      "text-offset": [0, 0.7],
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": "#ffffff",
      "text-halo-color": "#000000",
      "text-halo-width": 2,
      "text-opacity": [
        "step",
        ["zoom"],
        0,
        11.4,
        [
          "case",
          ["coalesce", ["feature-state", "checked"], false],
          1,
          0,
        ],
      ],
    },
  };

  if (textFilter !== null) {
    textLayerSpec.filter = textFilter;
  }

  map.addLayer(textLayerSpec);
  map.setLayerZoomRange(SKOV_TEXT_LAYER_ID, 0, 24);

  const originalFormattedPriceProperty = isHectare
    ? "formattedOriginalHa"
    : "formattedOriginalM2";
  const originalUnitLabel = isHectare ? " kr/ha" : " kr/m²";
  const originalHoverTextExpression: any = [
    "case",
    ["all", ["coalesce", ["get", "hasIssue"], false], ["has", "issueText"]],
    ["get", "issueText"],
    ["!=", ["coalesce", ["get", "mapHoverDetail"], ""], ""],
    ["get", "mapHoverDetail"],
    [
      "concat",
      ["get", originalFormattedPriceProperty],
      originalUnitLabel,
      " (",
      ["get", "transactionDateFormatted"],
      ")",
    ],
  ];
  const originalHoverOpacityExpression: any = [
    "step",
    ["zoom"],
    0,
    11.4,
    [
      "case",
      [
        "all",
        ["coalesce", ["get", "hasIssue"], false],
        ["coalesce", ["feature-state", "hover"], false],
      ],
      1,
      [
        "all",
        ["coalesce", ["feature-state", "hover"], false],
        ["!=", ["coalesce", ["get", "mapHoverDetail"], ""], ""],
      ],
      1,
      [
        "all",
        ["coalesce", ["feature-state", "hover"], false],
        ["coalesce", ["feature-state", "checked"], false],
      ],
      1,
      0,
    ],
  ];

  if (!map.getLayer(SKOV_ORIGINAL_TEXT_LAYER_ID)) {
    const originalTextLayerSpec: any = {
      id: SKOV_ORIGINAL_TEXT_LAYER_ID,
      type: "symbol",
      source: SKOV_SOURCE_ID_HOVER,
      layout: {
        "text-field": originalHoverTextExpression,
        "text-font": ["Noto Sans Regular"],
        "text-size": 14,
        "text-anchor": "top",
        "text-offset": [0, 2.6],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#000000",
        "text-halo-color": "#ffffff",
        "text-halo-width": 2,
        "text-opacity": originalHoverOpacityExpression,
      },
    };
    map.addLayer(originalTextLayerSpec);
  } else {
    map.setLayoutProperty(
      SKOV_ORIGINAL_TEXT_LAYER_ID,
      "text-field",
      originalHoverTextExpression,
    );
    map.setLayoutProperty(
      SKOV_ORIGINAL_TEXT_LAYER_ID,
      "text-allow-overlap",
      true,
    );
    map.setLayoutProperty(
      SKOV_ORIGINAL_TEXT_LAYER_ID,
      "text-ignore-placement",
      true,
    );
    map.setPaintProperty(SKOV_ORIGINAL_TEXT_LAYER_ID, "text-color", "#000000");
    map.setPaintProperty(
      SKOV_ORIGINAL_TEXT_LAYER_ID,
      "text-halo-color",
      "#ffffff",
    );
    map.setPaintProperty(
      SKOV_ORIGINAL_TEXT_LAYER_ID,
      "text-opacity",
      originalHoverOpacityExpression,
    );
    map.setFilter(SKOV_ORIGINAL_TEXT_LAYER_ID, null);
    const originalTextLayer = map.getLayer(SKOV_ORIGINAL_TEXT_LAYER_ID);
    if (originalTextLayer) {
      map.setLayerZoomRange(SKOV_ORIGINAL_TEXT_LAYER_ID, 0, 24);
    }
  }
}

function updateSkovFeatureStates(
  map: MaplibreMap,
  properties: JPBReferenceProperty[],
  checkedItems: Set<string>,
) {
  const source = map.getSource(SKOV_SOURCE_ID) as GeoJSONSource;
  if (source && source.type === "geojson") {
    const data = (source as any)._data as any;
    if (
      data &&
      data.type === "FeatureCollection" &&
      Array.isArray(data.features)
    ) {
      let needsUpdate = false;
      data.features.forEach((feature: any) => {
        if (
          feature.properties?.hasBuilding &&
          !feature.properties?.hasIssue &&
          feature.properties?.documentID
        ) {
          const isChecked = checkedItems.has(feature.properties.documentID);
          const buildingLocked = feature.properties.buildingLocked;
          let newIconName: string | null = null;

          if (buildingLocked) {
            newIconName = JPB_ICON_LOCKED;
          } else if (isChecked) {
            newIconName = JPB_ICON_CHECKED;
          } else {
            newIconName = JPB_ICON_UNCHECKED;
          }

          if (feature.properties.buildingIconName !== newIconName) {
            feature.properties.buildingIconName = newIconName;
            needsUpdate = true;
          }
        }
      });

      if (needsUpdate) {
        source.setData({ ...data });
      }
    }
  }

  for (const prop of properties) {
    if (!prop.documentID) continue;
    const isChecked = checkedItems.has(prop.documentID);
    map.setFeatureState(
      { source: SKOV_SOURCE_ID, id: prop.documentID },
      { checked: isChecked, hover: false },
    );
    if (map.getSource(SKOV_SOURCE_ID_HOVER)) {
      map.setFeatureState(
        { source: SKOV_SOURCE_ID_HOVER, id: prop.documentID },
        { checked: isChecked, hover: false },
      );
    }
    map.setFeatureState(
      { source: SKOV_SOURCE_ID_POLY, id: prop.documentID },
      { checked: isChecked, hover: false },
    );
  }
}

export function removeJPBSkovDemoMapLayers(map: MaplibreMap | null | undefined) {
  if (!map) return;

  if (map.getLayer(SKOV_TEXT_LAYER_ID)) map.removeLayer(SKOV_TEXT_LAYER_ID);
  if (map.getLayer(SKOV_ORIGINAL_TEXT_LAYER_ID)) {
    map.removeLayer(SKOV_ORIGINAL_TEXT_LAYER_ID);
  }
  if (map.getSource(SKOV_SOURCE_ID_HOVER)) {
    map.removeSource(SKOV_SOURCE_ID_HOVER);
  }
  if (map.getLayer(SKOV_LAYER_ID_ISSUE)) map.removeLayer(SKOV_LAYER_ID_ISSUE);
  if (map.getLayer(SKOV_LAYER_ID_BUILDING)) {
    map.removeLayer(SKOV_LAYER_ID_BUILDING);
  }
  if (map.getLayer(SKOV_LAYER_ID)) map.removeLayer(SKOV_LAYER_ID);
  if (map.getLayer(SKOV_LAYER_ID_POLY + "-outline")) {
    map.removeLayer(SKOV_LAYER_ID_POLY + "-outline");
  }
  if (map.getLayer(SKOV_LAYER_ID_POLY)) map.removeLayer(SKOV_LAYER_ID_POLY);
  if (map.getSource(SKOV_SOURCE_ID_POLY)) {
    map.removeSource(SKOV_SOURCE_ID_POLY);
  }
  if (map.getSource(SKOV_SOURCE_ID)) map.removeSource(SKOV_SOURCE_ID);
}

export function setupJPBSkovDemoMapLayers(
  map: MaplibreMap,
  properties: JPBReferenceProperty[],
  checkedItems: Set<string>,
  priceMode: PriceMode,
  areaUnit: AreaUnit,
  handlers: JPBMapLayerHandlers,
  lockedDocumentIds: Set<string> = new Set(),
  mapAssumptions?: Partial<JPBAssumptions>,
): () => void {
  const geoJson = JPBManager.convertToGeoJSON(properties, {
    checkedItems,
    assumptions: mapAssumptions,
    priceMode,
  });
  const hoverLabelsGeoJson = JPBManager.buildJPBHoverLabelsGeoJSON(
    properties,
    {
      checkedItems,
      assumptions: mapAssumptions,
      priceMode,
      areaUnit,
    },
  );

  if (Array.isArray((geoJson as any)?.features)) {
    (geoJson as any).features = (geoJson as any).features.map((feature: any) => {
      const documentID = feature?.properties?.documentID;
      if (!documentID || !feature?.properties) {
        return feature;
      }

      const hasIssue = Boolean(feature.properties.hasIssue);
      const hasBuilding = Boolean(feature.properties.hasBuilding);
      const isLocked =
        hasIssue || lockedDocumentIds.has(documentID);
      let buildingIconName: string | null =
        feature.properties.buildingIconName ?? null;

      if (hasBuilding && !hasIssue) {
        if (isLocked) {
          buildingIconName = JPB_ICON_LOCKED;
        } else if (checkedItems.has(documentID)) {
          buildingIconName = JPB_ICON_CHECKED;
        } else {
          buildingIconName = JPB_ICON_UNCHECKED;
        }
      } else {
        buildingIconName = null;
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          selectionLocked: isLocked,
          buildingLocked: hasBuilding && !hasIssue && isLocked,
          buildingIconName,
          buildingIconSize: hasBuilding && !hasIssue ? 1.0 : null,
        },
      };
    });
  }

  const geoJsonPoly = buildSkovPolyGeoJson(properties, checkedItems);

  if (map.getSource(SKOV_SOURCE_ID_POLY)) {
    const source = map.getSource(SKOV_SOURCE_ID_POLY) as GeoJSONSource;
    if (source) source.setData(geoJsonPoly as any);
  } else {
    map.addSource(SKOV_SOURCE_ID_POLY, {
      type: "geojson",
      promoteId: "documentID",
      data: geoJsonPoly as any,
    });
  }

  if (map.getSource(SKOV_SOURCE_ID)) {
    const source = map.getSource(SKOV_SOURCE_ID) as GeoJSONSource;
    if (source) source.setData(geoJson);
  } else {
    map.addSource(SKOV_SOURCE_ID, {
      type: "geojson",
      promoteId: "documentID",
      data: geoJson,
    });
  }

  if (map.getSource(SKOV_SOURCE_ID_HOVER)) {
    const h = map.getSource(SKOV_SOURCE_ID_HOVER) as GeoJSONSource;
    if (h) h.setData(hoverLabelsGeoJson as any);
  } else {
    map.addSource(SKOV_SOURCE_ID_HOVER, {
      type: "geojson",
      promoteId: "documentID",
      data: hoverLabelsGeoJson as any,
    });
  }

  const clickHandler = (e: any) => {
    if (e.originalEvent.defaultPrevented) return;
    e.originalEvent.preventDefault();
    e.preventDefault();
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const documentID = feature.properties?.documentID;
      if (documentID) {
        const ctrlKey = !!(
          e.originalEvent?.ctrlKey || e.originalEvent?.metaKey
        );
        handlers.onFeatureClick(documentID, ctrlKey);
      }
    }
    return false;
  };

  let hoveredFeatureId: string | number | null = null;

  const mouseMoveHandler = (e: any) => {
    map.getCanvas().style.cursor = "pointer";
    let newHoveredFeatureId: string | number | null = null;

    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      newHoveredFeatureId = feature.id;
    }

    if (hoveredFeatureId !== newHoveredFeatureId) {
      if (hoveredFeatureId !== null && hoveredFeatureId !== undefined) {
        map.setFeatureState(
          { source: SKOV_SOURCE_ID_POLY, id: hoveredFeatureId },
          { hover: false },
        );
        map.setFeatureState(
          { source: SKOV_SOURCE_ID, id: hoveredFeatureId },
          { hover: false },
        );
        map.setFeatureState(
          { source: SKOV_SOURCE_ID_HOVER, id: hoveredFeatureId },
          { hover: false },
        );
      }

      if (newHoveredFeatureId !== null && newHoveredFeatureId !== undefined) {
        map.setFeatureState(
          { source: SKOV_SOURCE_ID, id: newHoveredFeatureId },
          { hover: true },
        );
        map.setFeatureState(
          { source: SKOV_SOURCE_ID_HOVER, id: newHoveredFeatureId },
          { hover: true },
        );
        map.setFeatureState(
          { source: SKOV_SOURCE_ID_POLY, id: newHoveredFeatureId },
          { hover: true },
        );
        handlers.onFeatureHover?.(String(newHoveredFeatureId));
      } else {
        handlers.onFeatureHover?.(null);
      }

      hoveredFeatureId = newHoveredFeatureId;
    }
  };

  const mouseLeaveHandler = () => {
    map.getCanvas().style.cursor = "";
    if (hoveredFeatureId !== null && hoveredFeatureId !== undefined) {
      map.setFeatureState(
        { source: SKOV_SOURCE_ID, id: hoveredFeatureId },
        { hover: false },
      );
      map.setFeatureState(
        { source: SKOV_SOURCE_ID_HOVER, id: hoveredFeatureId },
        { hover: false },
      );
      map.setFeatureState(
        { source: SKOV_SOURCE_ID_POLY, id: hoveredFeatureId },
        { hover: false },
      );
      hoveredFeatureId = null;
      handlers.onFeatureHover?.(null);
    }
  };

  if (!map.getLayer(SKOV_LAYER_ID_POLY)) {
    const layerSpecPoly: any = {
      id: SKOV_LAYER_ID_POLY,
      type: "fill",
      source: SKOV_SOURCE_ID_POLY,
      paint: {
        "fill-color": [
          "case",
          ["to-boolean", ["feature-state", "checked"]],
          "#3a8e87",
          "#606060",
        ],
        "fill-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          13,
          0,
          14,
          0.5,
        ],
      },
    };
    map.addLayer(layerSpecPoly);
  }

  if (!map.getLayer(SKOV_LAYER_ID_POLY + "-outline")) {
    const layerSpecPolyOutline: any = {
      id: SKOV_LAYER_ID_POLY + "-outline",
      type: "line",
      source: SKOV_SOURCE_ID_POLY,
      paint: {
        "line-color": "#ffffff",
        "line-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          12.5,
          0,
          13.5,
          1,
        ],
        "line-width": [
          "case",
          ["to-boolean", ["feature-state", "hover"]],
          3,
          1,
        ],
      },
    };
    map.addLayer(layerSpecPolyOutline);
  }

  if (!map.getLayer(SKOV_LAYER_ID)) {
    const layerSpec: any = {
      id: SKOV_LAYER_ID,
      type: "circle",
      source: SKOV_SOURCE_ID,
      paint: {
        "circle-color": [
          "case",
          ["to-boolean", ["coalesce", ["get", "selectionLocked"], false]],
          "#1f1f1f",
          ["to-boolean", ["feature-state", "checked"]],
          "#3a8e87",
          "#606060",
        ],
        "circle-radius": [
          "case",
          ["to-boolean", ["feature-state", "hover"]],
          15,
          11,
        ],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#fff",
      },
    };
    map.addLayer(layerSpec);
  } else {
    map.setFilter(SKOV_LAYER_ID, null);
    map.setPaintProperty(
      SKOV_LAYER_ID,
      "circle-radius",
      [
        "case",
        ["to-boolean", ["feature-state", "hover"]],
        15,
        11,
      ],
    );
    map.setPaintProperty(SKOV_LAYER_ID, "circle-color", [
      "case",
      ["to-boolean", ["coalesce", ["get", "selectionLocked"], false]],
      "#1f1f1f",
      ["to-boolean", ["feature-state", "checked"]],
      "#3a8e87",
      "#606060",
    ]);
  }

  if (!map.getLayer(SKOV_LAYER_ID_ISSUE)) {
    const issueLayerSpec: any = {
      id: SKOV_LAYER_ID_ISSUE,
      type: "symbol",
      source: SKOV_SOURCE_ID,
      filter: [
        "all",
        ["==", ["get", "hasIssue"], true],
        ["==", ["get", "showPipelineIssueMarker"], true],
      ],
      layout: {
        "text-field": "!",
        "text-font": ["Noto Sans Bold"],
        "text-size": 18,
        "text-anchor": "center",
        "text-offset": [0, 0],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#ffffff",
        "text-opacity": [
          "case",
          ["to-boolean", ["feature-state", "hover"]],
          0.9,
          1,
        ],
      },
    };
    map.addLayer(issueLayerSpec);
  } else {
    map.setFilter(SKOV_LAYER_ID_ISSUE, [
      "all",
      ["==", ["get", "hasIssue"], true],
      ["==", ["get", "showPipelineIssueMarker"], true],
    ]);
  }

  if (!map.getLayer(SKOV_LAYER_ID_BUILDING)) {
    const buildingLayerSpec: any = {
      id: SKOV_LAYER_ID_BUILDING,
      type: "symbol",
      source: SKOV_SOURCE_ID,
      filter: [
        "all",
        ["==", ["get", "hasBuilding"], true],
        ["!=", ["get", "hasIssue"], true],
      ],
      layout: {
        "icon-image": [
          "coalesce",
          ["get", "buildingIconName"],
          "jpb-house-unchecked",
        ],
        "icon-size": ["coalesce", ["get", "buildingIconSize"], 1.0],
        "icon-anchor": "center",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        "icon-opacity": [
          "case",
          ["to-boolean", ["feature-state", "hover"]],
          0.9,
          1,
        ],
      },
    };
    map.addLayer(buildingLayerSpec);

    ensureJPBBuildingIcons(map).catch((err) => {
      console.error("Failed to pre-load JPB building icons:", err);
    });
  } else {
    map.setFilter(SKOV_LAYER_ID_BUILDING, [
      "all",
      ["==", ["get", "hasBuilding"], true],
      ["!=", ["get", "hasIssue"], true],
    ]);
  }

  updateSkovFeatureStates(map, properties, checkedItems);

  map.on("click", SKOV_LAYER_ID, clickHandler);
  map.on("mousemove", SKOV_LAYER_ID, mouseMoveHandler);
  map.on("mouseleave", SKOV_LAYER_ID, mouseLeaveHandler);

  map.on("click", SKOV_LAYER_ID_ISSUE, clickHandler);
  map.on("mousemove", SKOV_LAYER_ID_ISSUE, mouseMoveHandler);
  map.on("mouseleave", SKOV_LAYER_ID_ISSUE, mouseLeaveHandler);

  map.on("click", SKOV_LAYER_ID_BUILDING, clickHandler);
  map.on("mousemove", SKOV_LAYER_ID_BUILDING, mouseMoveHandler);
  map.on("mouseleave", SKOV_LAYER_ID_BUILDING, mouseLeaveHandler);

  map.on("click", SKOV_LAYER_ID_POLY, clickHandler);
  map.on("mousemove", SKOV_LAYER_ID_POLY, mouseMoveHandler);
  map.on("mouseleave", SKOV_LAYER_ID_POLY, mouseLeaveHandler);

  setupSkovTextLayers(map, priceMode, areaUnit);

  return () => {
    if (map.getLayer(SKOV_LAYER_ID)) {
      map.off("click", SKOV_LAYER_ID, clickHandler);
      map.off("mousemove", SKOV_LAYER_ID, mouseMoveHandler);
      map.off("mouseleave", SKOV_LAYER_ID, mouseLeaveHandler);
    }
    if (map.getLayer(SKOV_LAYER_ID_ISSUE)) {
      map.off("click", SKOV_LAYER_ID_ISSUE, clickHandler);
      map.off("mousemove", SKOV_LAYER_ID_ISSUE, mouseMoveHandler);
      map.off("mouseleave", SKOV_LAYER_ID_ISSUE, mouseLeaveHandler);
    }
    if (map.getLayer(SKOV_LAYER_ID_BUILDING)) {
      map.off("click", SKOV_LAYER_ID_BUILDING, clickHandler);
      map.off("mousemove", SKOV_LAYER_ID_BUILDING, mouseMoveHandler);
      map.off("mouseleave", SKOV_LAYER_ID_BUILDING, mouseLeaveHandler);
    }
    if (map.getLayer(SKOV_LAYER_ID_POLY)) {
      map.off("click", SKOV_LAYER_ID_POLY, clickHandler);
      map.off("mousemove", SKOV_LAYER_ID_POLY, mouseMoveHandler);
      map.off("mouseleave", SKOV_LAYER_ID_POLY, mouseLeaveHandler);
    }
    map.getCanvas().style.cursor = "";

    removeJPBSkovDemoMapLayers(map);
  };
}

export function highlightSkovPolygonOnMap(
  map: MaplibreMap,
  geometry: any,
  documentID: string,
  timeoutMs: number = 5000,
): () => void {
  if (map.getLayer(SKOV_LAYER_ID_HIGHLIGHT)) {
    map.removeLayer(SKOV_LAYER_ID_HIGHLIGHT);
  }
  if (map.getSource(SKOV_SOURCE_ID_HIGHLIGHT)) {
    map.removeSource(SKOV_SOURCE_ID_HIGHLIGHT);
  }

  const highlightFeature = {
    type: "Feature" as const,
    id: documentID,
    geometry,
    properties: {
      documentID,
    },
  };

  map.addSource(SKOV_SOURCE_ID_HIGHLIGHT, {
    type: "geojson",
    data: {
      type: "FeatureCollection" as const,
      features: [highlightFeature],
    },
  });

  map.addLayer({
    id: SKOV_LAYER_ID_HIGHLIGHT,
    type: "line",
    source: SKOV_SOURCE_ID_HIGHLIGHT,
    paint: {
      "line-color": "#22c55e",
      "line-width": 5,
      "line-opacity": 1,
    },
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
  });

  const timeoutId = setTimeout(() => {
    if (map.getLayer(SKOV_LAYER_ID_HIGHLIGHT)) {
      map.removeLayer(SKOV_LAYER_ID_HIGHLIGHT);
    }
    if (map.getSource(SKOV_SOURCE_ID_HIGHLIGHT)) {
      map.removeSource(SKOV_SOURCE_ID_HIGHLIGHT);
    }
  }, timeoutMs);

  return () => {
    clearTimeout(timeoutId);
    if (map.getLayer(SKOV_LAYER_ID_HIGHLIGHT)) {
      map.removeLayer(SKOV_LAYER_ID_HIGHLIGHT);
    }
    if (map.getSource(SKOV_SOURCE_ID_HIGHLIGHT)) {
      map.removeSource(SKOV_SOURCE_ID_HIGHLIGHT);
    }
  };
}
